import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import "./RegisterForm.css";
import logo from "../assets/a0b9f44cc8f2da963034bf8a0849385a074bd917.png";

interface FormData {
  full_name: string;
  mobile_number: string;
  alternate_mobile_number: string;
  email: string;
  state: string;
  district: string;
  taluka: string;
  blood_group: string;
  bank_name: string;
  account_number: string;
  ifsc: string;
  adhar_card_file: File | null;
  pan_card_file: File | null;
  education_document_file: File | null;
  adhar_card_number: string;
  pan_card_number: string;
  otp: string;
}

interface FormErrors {
  [key: string]: string | undefined;
  full_name?: string;
  mobile_number?: string;
  email?: string;
  blood_group?: string;
  state?: string;
  district?: string;
  taluka?: string;
  bank_name?: string;
  account_number?: string;
  ifsc?: string;
  adhar_card_file?: string;
  pan_card_file?: string;
  education_document_file?: string;
  adhar_card_number?: string;
  pan_card_number?: string;
  alternate_mobile_number?: string;
  otp?: string;
}

interface OTPState {
  sent: boolean;
  verified: boolean;
  countdown: number;
  attempts: number;
}

const RegisterForm: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [otpState, setOtpState] = useState<OTPState>({
    sent: false,
    verified: false,
    countdown: 0,
    attempts: 0
  });

  const [form, setForm] = useState<FormData>({
    full_name: "",
    mobile_number: "",
    alternate_mobile_number: "",
    email: "",
    state: "",
    district: "",
    taluka: "",
    blood_group: "",
    bank_name: "",
    account_number: "",
    ifsc: "",
    adhar_card_file: null,
    pan_card_file: null,
    education_document_file: null,
    adhar_card_number: "",
    pan_card_number: "",
    otp: ""
  });

  // Countdown timer effect
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (otpState.countdown > 0) {
      interval = setInterval(() => {
        setOtpState(prev => ({
          ...prev,
          countdown: prev.countdown - 1
        }));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [otpState.countdown]);

  // Options data
  const bloodGroups = ["A+", "A-", "B+", "B-", "O+", "O-", "AB+", "AB-"];
  const states = ["Maharashtra", "Gujarat", "Karnataka", "Tamil Nadu", "Delhi"];
  const districts = [
    "Ahmednagar",
    "Akola",
    "Amravati",
    "Aurangabad",
    "Beed",
    "Bhandara",
    "Buldhana",
    "Chandrapur",
    "Dhule",
    "Gadchiroli",
    "Gondia",
    "Hingoli",
    "Jalgaon",
    "Jalna",
    "Kolhapur",
    "Latur",
    "Mumbai City",
    "Mumbai Suburban",
    "Nagpur",
    "Nanded",
    "Nandurbar",
    "Nashik",
    "Osmanabad",
    "Palghar",
    "Parbhani",
    "Pune",
    "Raigad",
    "Ratnagiri",
    "Sangli",
    "Satara",
    "Sindhudurg",
    "Solapur",
    "Thane",
    "Wardha",
    "Washim",
    "Yavatmal"
  ];
    const talukas = ["City", "Suburban", "Rural"];

  // Input change handler
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    
    // Only allow numbers for mobile and OTP fields
    if (name === 'mobile_number' || name === 'alternate_mobile_number') {
      const numericValue = value.replace(/\D/g, '');
      setForm((prev) => ({ ...prev, [name]: numericValue }));
      
      // Reset OTP state if mobile number changes
      if (name === 'mobile_number' && otpState.sent) {
        setOtpState({
          sent: false,
          verified: false,
          countdown: 0,
          attempts: 0
        });
        setForm(prev => ({ ...prev, otp: "" }));
      }
    } else if (name === 'otp') {
      const numericValue = value.replace(/\D/g, '');
      setForm((prev) => ({ ...prev, [name]: numericValue }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, files } = e.target;
    if (files && files[0]) {
      const file = files[0];
      
      if (file.size > 5 * 1024 * 1024) {
        setErrors((prev) => ({ 
          ...prev, 
          [name]: "File size must be less than 5MB" 
        }));
        return;
      }
      const validTypes = ["image/jpeg", "image/jpg", "image/png", "application/pdf"];
      if (!validTypes.includes(file.type)) {
        setErrors((prev) => ({ 
          ...prev, 
          [name]: "Only PDF, JPG, and PNG files are allowed" 
        }));
        return;
      }

      setForm((prev) => ({ ...prev, [name]: file }));
        if (errors[name]) {
        setErrors((prev) => ({ ...prev, [name]: undefined }));
      }
    }
  };

  // Show SweetAlert notification
  const showAlert = (title: string, text: string, icon: 'success' | 'error' | 'warning' | 'info' = 'info') => {
    Swal.fire({
      title,
      text,
      icon,
      confirmButtonColor: '#3085d6',
      confirmButtonText: 'OK'
    });
  };

  // Send OTP function
  const sendOTP = async () => {
    if (!form.mobile_number.trim()) {
      setErrors(prev => ({ ...prev, mobile_number: "Mobile number is required" }));
      return;
    }
    
    if (!/^\d{10}$/.test(form.mobile_number)) {
      setErrors(prev => ({ ...prev, mobile_number: "Invalid mobile number (10 digits required)" }));
      return;
    }

    setOtpLoading(true);
    
    try {
      const response = await fetch("https://www.crm.brickfolio.in/api/global-partner-register-send-otp", {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify({ mobile_number: form.mobile_number })
      });

      const data = await response.json();
      
      if (response.ok) {
        setOtpState({
          sent: true,
          verified: false,
          countdown: 60, 
          attempts: 0
        });
        
        // Clear any previous OTP errors
        setErrors(prev => ({ ...prev, mobile_number: undefined }));
        
        // Show success message
        showAlert("OTP Sent!", "OTP has been sent to your mobile number", "success");
      } else {
        throw new Error(data.message || "Failed to send OTP");
      }
      
    } catch (error) {
      console.error("OTP sending error:", error);
      const errorMessage = error instanceof Error ? error.message : "Failed to send OTP. Please try again.";
      setErrors(prev => ({ ...prev, mobile_number: errorMessage }));
      showAlert("Error", errorMessage, "error");
    } finally {
      setOtpLoading(false);
    }
  };

  // Verify OTP function
  const verifyOTP = async () => {
    if (!form.otp.trim()) {
      setErrors(prev => ({ ...prev, otp: "OTP is required" }));
      return;
    }

    if (!/^\d{4}$/.test(form.otp)) {
      setErrors(prev => ({ ...prev, otp: "OTP must be 4 digits" }));
      return;
    }

    setOtpLoading(true);
    
    try {
      const response = await fetch("https://www.crm.brickfolio.in/api/global-partner-verify-user-otp", {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify({ 
          mobile_number: form.mobile_number,
          otp: form.otp 
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        setOtpState(prev => ({
          ...prev,
          verified: true,
          attempts: prev.attempts + 1
        }));
        
        // Clear OTP errors
        setErrors(prev => ({ ...prev, otp: undefined, mobile_number: undefined }));
        
        // Show success message
        showAlert("Verified!", "Mobile number verified successfully", "success");
      } else {
        throw new Error(data.message || "Invalid OTP");
      }
      
    } catch (error) {
      console.error("OTP verification error:", error);
      const errorMessage = error instanceof Error ? error.message : "Invalid OTP. Please try again.";
      setErrors(prev => ({ ...prev, otp: errorMessage }));
      setOtpState(prev => ({
        ...prev,
        attempts: prev.attempts + 1
      }));
      showAlert("Verification Failed", errorMessage, "error");
    } finally {
      setOtpLoading(false);
    }
  };

  // Resend OTP function
  const resendOTP = async () => {
    if (otpState.countdown > 0) return;
    
    setOtpLoading(true);
    
    try {
      const response = await fetch("https://www.crm.brickfolio.in/api/global-partner-register-send-otp", {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify({ mobile_number: form.mobile_number })
      });

      const data = await response.json();
      
      if (response.ok) {
        setOtpState(prev => ({
          ...prev,
          countdown: 60
        }));
        
        // Show success message
        showAlert("OTP Resent!", "New OTP has been sent to your mobile number", "success");
      } else {
        throw new Error(data.message || "Failed to resend OTP");
      }
      
    } catch (error) {
      console.error("OTP resend error:", error);
      const errorMessage = error instanceof Error ? error.message : "Failed to resend OTP. Please try again.";
      setErrors(prev => ({ ...prev, mobile_number: errorMessage }));
      showAlert("Error", errorMessage, "error");
    } finally {
      setOtpLoading(false);
    }
  };

  // Validation functions
  const validateStep1 = (): boolean => {
    const newErrors: FormErrors = {};
    
    if (!form.full_name.trim()) {
      newErrors.full_name = "Full name is required";
    } else if (form.full_name.trim().length < 2) {
      newErrors.full_name = "Full name must be at least 2 characters";
    }
    
    if (!form.mobile_number.trim()) {
      newErrors.mobile_number = "Mobile number is required";
    } else if (!/^\d{10}$/.test(form.mobile_number)) {
      newErrors.mobile_number = "Invalid mobile number (10 digits required)";
    }
    
    // Check if mobile number is verified
    if (!otpState.verified) {
      newErrors.mobile_number = "Please verify your mobile number with OTP";
    }
    
    if (form.alternate_mobile_number && !/^\d{10}$/.test(form.alternate_mobile_number)) {
      newErrors.alternate_mobile_number = "Invalid mobile number (10 digits required)";
    }
    
    if (!form.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^\S+@\S+\.\S+$/.test(form.email)) {
      newErrors.email = "Invalid email format";
    }
    
    if (!form.blood_group) {
      newErrors.blood_group = "Blood group is required";
    }
    
    if (form.adhar_card_number && !/^\d{12}$/.test(form.adhar_card_number)) {
      newErrors.adhar_card_number = "Aadhar must be 12 digits";
    }
    
    if (form.pan_card_number && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(form.pan_card_number)) {
      newErrors.pan_card_number = "Invalid PAN format (e.g., ABCDE1234F)";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = (): boolean => {
    const newErrors: FormErrors = {};
    
    if (!form.state) newErrors.state = "State is required";
    if (!form.district) newErrors.district = "District is required";
    if (!form.taluka) newErrors.taluka = "Taluka is required";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep3 = (): boolean => {
    const newErrors: FormErrors = {};
    
    if (!form.bank_name.trim()) {
      newErrors.bank_name = "Bank name is required";
    }
    
    if (!form.account_number.trim()) {
      newErrors.account_number = "Account number is required";
    } else if (!/^\d{9,18}$/.test(form.account_number)) {
      newErrors.account_number = "Invalid account number (9-18 digits)";
    }
    
    if (!form.ifsc.trim()) {
      newErrors.ifsc = "IFSC code is required";
    } else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(form.ifsc)) {
      newErrors.ifsc = "Invalid IFSC format (e.g., SBIN0000123)";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep4 = (): boolean => {
    const newErrors: FormErrors = {};
    
    if (!form.adhar_card_file) newErrors.adhar_card_file = "Aadhar card is required";
    if (!form.pan_card_file) newErrors.pan_card_file = "PAN card is required";
    if (!form.education_document_file) {
      newErrors.education_document_file = "Education document is required";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Navigation handlers
  const nextStep = () => {
    let isValid = false;
    
    switch (step) {
      case 1:
        isValid = validateStep1();
        break;
      case 2:
        isValid = validateStep2();
        break;
      case 3:
        isValid = validateStep3();
        break;
      case 4:
        isValid = validateStep4();
        break;
      default:
        isValid = true;
    }
    
    if (isValid) {
      setStep((prev) => Math.min(prev + 1, 5));
    } else {
      showAlert("Validation Error", "Please fill all required fields correctly before proceeding.", "warning");
    }
  };

  const prevStep = () => {
    setStep((prev) => Math.max(prev - 1, 1));
  };

  // Helper functions
  const getFileName = (file: File | null): string => {
    return file ? file.name : "No file chosen";
  };

  // Form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate all steps before submission
    const isStep1Valid = validateStep1();
    const isStep2Valid = validateStep2();
    const isStep3Valid = validateStep3();
    const isStep4Valid = validateStep4();

    if (!isStep1Valid || !isStep2Valid || !isStep3Valid || !isStep4Valid) {
      showAlert("Validation Error", "Please fill all required fields correctly before submitting.", "warning");
      return;
    }

    // Ensure mobile number is verified
    if (!otpState.verified) {
      showAlert("Verification Required", "Please verify your mobile number before submitting.", "warning");
      setStep(1);
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();
      
      // Append all form data
      Object.entries(form).forEach(([key, value]) => {
        if (value instanceof File) {
          formData.append(key, value);
        } else if (value !== null && value !== undefined && key !== 'otp') {
          formData.append(key, value.toString());
        }
      });

      const response = await fetch("https://www.crm.brickfolio.in/api/user-register", {
        method: "POST",
        body: formData,
      });

      const data = await response.json();
      
      if (response.ok && data.status === 'success') {
        // Show success message with SweetAlert
        Swal.fire({
          title: "Registration Successful!",
          text: "Your account has been created successfully.",
          icon: "success",
          confirmButtonColor: "#3085d6",
          confirmButtonText: "Continue to Login"
        }).then((result) => {
          if (result.isConfirmed) {
            navigate("/");
          }
        });
      } else {
        throw new Error(data.message || "Registration failed");
      }
    } catch (error) {
      console.error("Registration error:", error);
      const errorMessage = error instanceof Error ? error.message : "Something went wrong. Please try again.";
      showAlert("Registration Failed", errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  // Step titles for better UX
  const stepTitles = ["Personal", "Address", "Bank", "Documents", "Overview"];

  return (
    <div className="reg-wrapper">
      <div className="reg-card-form">
        <img src={logo} className="reg-logo" alt="Company Logo" />
        <h2 className="reg-title">Create Account</h2>
        <p className="reg-sub">Join us today</p>

        {/* Steps Indicator */}
        <div className="steps-row">
          {stepTitles.map((title, index) => (
            <span
              key={title}
              className={`step-indicator ${step === index + 1 ? "active-step" : ""} ${
                step > index + 1 ? "completed-step" : ""
              }`}
            >
              {title}
            </span>
          ))}
        </div>

        <form onSubmit={handleSubmit}>
          {/* Step 1: Personal Details */}
          {step === 1 && (
            <div className="form-step">
              <h3>Personal Information</h3>
              
              {/* Mobile Number with OTP Verification */}
              <div className="form-row">
                <div className="col-md-6">
                  <div className="form-group">
                    <label>Mobile Number *</label>
                    <div className="mobile-verify-container">
                      <input
                        name="mobile_number"
                        value={form.mobile_number}
                        onChange={handleChange}
                        placeholder="Enter 10-digit number"
                        maxLength={10}
                        className={`${errors.mobile_number ? "error" : ""} ${otpState.verified ? "verified" : ""}`}
                        disabled={otpState.verified || otpLoading}
                      />
                      {!otpState.verified && (
                        <button
                          type="button"
                          className="otp-btn"
                          onClick={sendOTP}
                          disabled={otpLoading || !form.mobile_number || !/^\d{10}$/.test(form.mobile_number)}
                        >
                          {otpLoading ? "Sending..." : otpState.sent ? "Resend OTP" : "Send OTP"}
                        </button>
                      )}
                      {otpState.verified && (
                        <span className="verified-badge">✓ Verified</span>
                      )}
                    </div>
                    {errors.mobile_number && (
                      <span className="error-text">{errors.mobile_number}</span>
                    )}
                  </div>
                </div>

                {/* OTP Input Field */}
                {otpState.sent && !otpState.verified && (
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Enter OTP *</label>
                      <div className="otp-input-container">
                        <input
                          name="otp"
                          value={form.otp}
                          onChange={handleChange}
                          placeholder="Enter 4-digit OTP"
                          maxLength={4}
                          className={errors.otp ? "error" : ""}
                          disabled={otpLoading}
                        />
                        <button
                          type="button"
                          className="verify-otp-btn"
                          onClick={verifyOTP}
                          disabled={otpLoading || !form.otp || !/^\d{4}$/.test(form.otp)}
                        >
                          {otpLoading ? "Verifying..." : "Verify OTP"}
                        </button>
                      </div>
                      {errors.otp && (
                        <span className="error-text">{errors.otp}</span>
                      )}
                      {otpState.countdown > 0 ? (
                        <div className="otp-timer">
                          Resend OTP in {otpState.countdown}s
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="resend-otp-link"
                          onClick={resendOTP}
                          disabled={otpLoading}
                        >
                          Resend OTP
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="form-row">
                <div className="col-md-4">
                  <div className="form-group">
                    <label>Full Name *</label>
                    <input
                      name="full_name"
                      value={form.full_name}
                      onChange={handleChange}
                      placeholder="Enter your full name"
                      className={errors.full_name ? "error" : ""}
                    />
                    {errors.full_name && (
                      <span className="error-text">{errors.full_name}</span>
                    )}
                  </div>
                </div>
                
                <div className="col-md-4">
                  <div className="form-group">
                    <label>Alternate Mobile Number</label>
                    <input
                      name="alternate_mobile_number"
                      value={form.alternate_mobile_number}
                      onChange={handleChange}
                      placeholder="Enter 10-digit number"
                      maxLength={10}
                      className={errors.alternate_mobile_number ? "error" : ""}
                    />
                    {errors.alternate_mobile_number && (
                      <span className="error-text">{errors.alternate_mobile_number}</span>
                    )}
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-group">
                    <label>Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder="your.email@example.com"
                      className={errors.email ? "error" : ""}
                    />
                    {errors.email && (
                      <span className="error-text">{errors.email}</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="form-row">
                <div className="col-md-4">
                  <div className="form-group">
                    <label>Blood Group *</label>
                    <select
                      name="blood_group"
                      value={form.blood_group}
                      onChange={handleChange}
                      className={errors.blood_group ? "error" : ""}
                    >
                      <option value="">Select Blood Group</option>
                      {bloodGroups.map((group) => (
                        <option key={group} value={group}>
                          {group}
                        </option>
                      ))}
                    </select>
                    {errors.blood_group && (
                      <span className="error-text">{errors.blood_group}</span>
                    )}
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-group">
                    <label>Aadhar Card Number</label>
                    <input
                      name="adhar_card_number"
                      value={form.adhar_card_number}
                      onChange={handleChange}
                      placeholder="Enter 12-digit number"
                      maxLength={12}
                      className={errors.adhar_card_number ? "error" : ""}
                    />
                    {errors.adhar_card_number && (
                      <span className="error-text">{errors.adhar_card_number}</span>
                    )}
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-group">
                    <label>PAN Card Number</label>
                    <input
                      name="pan_card_number"
                      value={form.pan_card_number}
                      onChange={handleChange}
                      placeholder="Enter PAN (e.g., ABCDE1234F)"
                      maxLength={10}
                      className={errors.pan_card_number ? "error" : ""}
                    />
                    {errors.pan_card_number && (
                      <span className="error-text">{errors.pan_card_number}</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="mobile-btn-container">
                <button 
                  type="button" 
                  className="reg-btn" 
                  onClick={nextStep}
                  disabled={!otpState.verified}
                >
                  Next
                </button>
                {!otpState.verified && (
                  <div className="verification-warning">
                    Please verify your mobile number to continue
                  </div>
                )}
              </div>
            </div>
          )}

           {/* Step 2: Address Details */}
           {step === 2 && (
            <div className="form-step">
              <h3>Address Information</h3>
              <div className="form-row">
                <div className="col-md-4">
                  <div className="form-group">
                    <label>State *</label>
                    <select
                      name="state"
                      value={form.state}
                      onChange={handleChange}
                      className={errors.state ? "error" : ""}
                      disabled={loading}
                    >
                      <option value="">Select State</option>
                      {states.map((state) => (
                        <option key={state} value={state}>
                          {state}
                        </option>
                      ))}
                    </select>
                    {errors.state && (
                      <span className="error-text">{errors.state}</span>
                    )}
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-group">
                    <label>District *</label>
                    <select
                      name="district"
                      value={form.district}
                      onChange={handleChange}
                      className={errors.district ? "error" : ""}
                      disabled={loading}
                    >
                      <option value="">Select District</option>
                      {districts.map((district) => (
                        <option key={district} value={district}>
                          {district}
                        </option>
                      ))}
                    </select>
                    {errors.district && (
                      <span className="error-text">{errors.district}</span>
                    )}
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-group">
                    <label>Taluka *</label>
                    <select
                      name="taluka"
                      value={form.taluka}
                      onChange={handleChange}
                      className={errors.taluka ? "error" : ""}
                      disabled={loading}
                    >
                      <option value="">Select Taluka</option>
                      {talukas.map((taluka) => (
                        <option key={taluka} value={taluka}>
                          {taluka}
                        </option>
                      ))}
                    </select>
                    {errors.taluka && (
                      <span className="error-text">{errors.taluka}</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="mobile-btn-container">
                <div className="btn-row">
                  <button
                    type="button"
                    className="reg-btn-outline"
                    onClick={prevStep}
                    disabled={loading}
                  >
                    Back
                  </button>
                  <button 
                    type="button" 
                    className="reg-btn" 
                    onClick={nextStep}
                    disabled={loading}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Bank Details */}
          {step === 3 && (
            <div className="form-step">
              <h3>Bank Information</h3>
              <div className="form-row">
                <div className="col-md-4">
                  <div className="form-group">
                    <label>Bank Name *</label>
                    <input
                      name="bank_name"
                      value={form.bank_name}
                      onChange={handleChange}
                      placeholder="Enter bank name"
                      className={errors.bank_name ? "error" : ""}
                      disabled={loading}
                    />
                    {errors.bank_name && (
                      <span className="error-text">{errors.bank_name}</span>
                    )}
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-group">
                    <label>Account Number *</label>
                    <input
                      name="account_number"
                      value={form.account_number}
                      onChange={handleChange}
                      placeholder="Enter account number"
                      className={errors.account_number ? "error" : ""}
                      disabled={loading}
                    />
                    {errors.account_number && (
                      <span className="error-text">
                        {errors.account_number}
                      </span>
                    )}
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-group">
                    <label>IFSC Code *</label>
                    <input
                      name="ifsc"
                      value={form.ifsc}
                      onChange={handleChange}
                      placeholder="Enter IFSC code"
                      className={errors.ifsc ? "error" : ""}
                      disabled={loading}
                    />
                    {errors.ifsc && (
                      <span className="error-text">{errors.ifsc}</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="mobile-btn-container">
                <div className="btn-row">
                  <button
                    type="button"
                    className="reg-btn-outline"
                    onClick={prevStep}
                    disabled={loading}
                  >
                    Back
                  </button>
                  <button 
                    type="button" 
                    className="reg-btn" 
                    onClick={nextStep}
                    disabled={loading}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Document Upload */}
          {step === 4 && (
            <div className="form-step">
              <h3>Document Upload</h3>
              <div className="form-row">
                <div className="col-md-4">
                  <div className="form-group">
                    <label>Aadhar Card *</label>
                    <div className="file-upload-wrapper">
                      <input
                        type="file"
                        name="adhar_card_file"
                        onChange={handleFileChange}
                        accept=".pdf,.jpg,.jpeg,.png"
                        className="file-input"
                        disabled={loading}
                      />
                      <div className="file-display">
                        {getFileName(form.adhar_card_file)}
                      </div>
                    </div>
                    {errors.adhar_card_file && (
                      <span className="error-text">
                        {errors.adhar_card_file}
                      </span>
                    )}
                    <small>PDF, JPG, PNG (Max 5MB)</small>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-group">
                    <label>PAN Card *</label>
                    <div className="file-upload-wrapper">
                      <input
                        type="file"
                        name="pan_card_file"
                        onChange={handleFileChange}
                        accept=".pdf,.jpg,.jpeg,.png"
                        className="file-input"
                        disabled={loading}
                      />
                      <div className="file-display">
                        {getFileName(form.pan_card_file)}
                      </div>
                    </div>
                    {errors.pan_card_file && (
                      <span className="error-text">{errors.pan_card_file}</span>
                    )}
                    <small>PDF, JPG, PNG (Max 5MB)</small>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-group">
                    <label>Education Document *</label>
                    <div className="file-upload-wrapper">
                      <input
                        type="file"
                        name="education_document_file"
                        onChange={handleFileChange}
                        accept=".pdf,.jpg,.jpeg,.png"
                        className="file-input"
                        disabled={loading}
                      />
                      <div className="file-display">
                        {getFileName(form.education_document_file)}
                      </div>
                    </div>
                    {errors.education_document_file && (
                      <span className="error-text">
                        {errors.education_document_file}
                      </span>
                    )}
                    <small>PDF, JPG, PNG (Max 5MB)</small>
                  </div>
                </div>
              </div>

              <div className="mobile-btn-container">
                <div className="btn-row">
                  <button
                    type="button"
                    className="reg-btn-outline"
                    onClick={prevStep}
                    disabled={loading}
                  >
                    Back
                  </button>
                  <button 
                    type="button" 
                    className="reg-btn" 
                    onClick={nextStep}
                    disabled={loading}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Step 5: Overview */}
          {step === 5 && (
            <div className="form-step">
              <h3>Review Details</h3>
              <div className="review-box">
                <h4>Personal Information</h4>
                <div className="review-row">
                  <div className="review-col">
                    <p>
                      <strong>Full Name:</strong> {form.full_name}
                    </p>
                    <p>
                      <strong>Mobile:</strong> {form.mobile_number}
                      {otpState.verified && (
                        <span className="verified-tag"> ✓ Verified</span>
                      )}
                    </p>
                    <p>
                      <strong>Email:</strong> {form.email}
                    </p>
                    <p>
                      <strong>Aadhar Number:</strong>{" "}
                      {form.adhar_card_number || "Not provided"}
                    </p>
                  </div>
                  <div className="review-col">
                    <p>
                      <strong>Alternate Mobile:</strong>{" "}
                      {form.alternate_mobile_number || "Not provided"}
                    </p>
                    <p>
                      <strong>Blood Group:</strong> {form.blood_group}
                    </p>
                    <p>
                      <strong>PAN Number:</strong>{" "}
                      {form.pan_card_number || "Not provided"}
                    </p>
                  </div>
                </div>

                <h4>Address Information</h4>
                <div className="review-row">
                  <div className="review-col">
                    <p>
                      <strong>State:</strong> {form.state}
                    </p>
                    <p>
                      <strong>District:</strong> {form.district}
                    </p>
                  </div>
                  <div className="review-col">
                    <p>
                      <strong>Taluka:</strong> {form.taluka}
                    </p>
                  </div>
                </div>

                <h4>Bank Information</h4>
                <div className="review-row">
                  <div className="review-col">
                    <p>
                      <strong>Bank Name:</strong> {form.bank_name}
                    </p>
                    <p>
                      <strong>Account Number:</strong> {form.account_number}
                    </p>
                  </div>
                  <div className="review-col">
                    <p>
                      <strong>IFSC Code:</strong> {form.ifsc}
                    </p>
                  </div>
                </div>

                <h4>Documents</h4>
                <div className="review-row">
                  <div className="review-col">
                    <p>
                      <strong>Aadhar Card:</strong>{" "}
                      {getFileName(form.adhar_card_file)}
                    </p>
                    <p>
                      <strong>PAN Card:</strong>{" "}
                      {getFileName(form.pan_card_file)}
                    </p>
                  </div>
                  <div className="review-col">
                    <p>
                      <strong>Education Document:</strong>{" "}
                      {getFileName(form.education_document_file)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="mobile-btn-container">
                <div className="btn-row">
                  <button
                    type="button"
                    className="reg-btn-outline"
                    onClick={prevStep}
                    disabled={loading}
                  >
                    Back
                  </button>
                  <button className="reg-btn" type="submit" disabled={loading}>
                    {loading ? "Please wait..." : "Submit Registration"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default RegisterForm;